import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  RequestCodeBody,
  RequestCodeResponse,
  SsoAuthorizationResponse,
  SsoStatusResponse,
  VerifyCodeBody,
  VerifyCodeResponse,
} from './types';

/**
 * Authentication lives on the backend service (`VITE_BACKEND_URI`), not the
 * api-service every other v2 call goes to, so each request passes its base URL
 * explicitly.
 */

export async function requestLoginCode(
  body: RequestCodeBody,
): Promise<RequestCodeResponse> {
  return apiFetch<RequestCodeResponse>(
    '/authentication/request-code',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function verifyLoginCode(
  body: VerifyCodeBody,
): Promise<VerifyCodeResponse> {
  return apiFetch<VerifyCodeResponse>(
    '/authentication/login',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/**
 * Revokes the current session server-side. Authenticated by the bearer token
 * `apiFetch` attaches, so it has to be called *before* the local session is
 * cleared.
 */
export async function revokeSession(): Promise<void> {
  await apiFetch<void>(
    '/authentication/logout',
    { method: 'POST' },
    BACKEND_URL,
  );
}

/** `domain` and `search` arrive already encoded — see `app/login/-utils/email.ts`. */
export async function fetchSsoAuthorization(
  domain: string,
  search: string,
): Promise<SsoAuthorizationResponse> {
  return apiFetch<SsoAuthorizationResponse>(
    `/authentication/single-sign-on/${domain}?${search}`,
    { method: 'GET' },
    BACKEND_URL,
  );
}

/**
 * Exchanges the one-time `sso_code` the OAuth2 callback redirected here with
 * for the actual session — same response shape as `/authentication/login`.
 */
export async function exchangeSsoCode(
  code: string,
): Promise<VerifyCodeResponse> {
  return apiFetch<VerifyCodeResponse>(
    `/authentication/sso-code?code=${encodeURIComponent(code)}`,
    { method: 'GET' },
    BACKEND_URL,
  );
}

/**
 * Whether the domain must authenticate through SSO.
 *
 * A dedicated status endpoint rather than the single-sign-on route below: that
 * one *starts* a login (it mints an authorization URL against the identity
 * provider), so calling it while someone is still typing their address would
 * open flows nobody asked for.
 *
 * `search` arrives already encoded — see `app/login/-utils/email.ts`.
 */
export async function fetchSsoStatus(
  search: string,
): Promise<SsoStatusResponse> {
  return apiFetch<SsoStatusResponse>(
    `/authentication/sso-status?${search}`,
    { method: 'GET' },
    BACKEND_URL,
  );
}
